import React, { useState, useEffect, useMemo } from 'react';
import { useStorefrontSettings } from '../../hooks/StorefrontSettingsContext';
import Storefront from '../../services/Storefront';
import Box from '@mui/material/Box';
import Loading from '../Loading';
import Button from '@mui/material/Button';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import Paper from '@mui/material/Paper';
import Notifications from '../../services/Notifications';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import TranslatableDataGrid from '../shared/TranslatableDataGrid';
import { ThemeProvider } from '@mui/material/styles';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import Typography from '@mui/material/Typography';
import { InputLabel, Select, MenuItem } from '@mui/material';
import MUIModal from '../shared/MUIModal';
import MUIAlertModal from '../shared/MUIAlertModal';


export const FreightHandlingCodeAdministration = () => {
    const [rows, setRows] = useState([]);
    const [fetching, setFetching] = useState(false);
    const localized = useStorefrontAdministrationConfig().labels;
    const storeData = useStorefrontSettings();
    const [freightHandlingCode, setFreightHandlingCode] = useState('');
    const [description, setDescription] = useState('');
    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [validFreightHandlingCodes, setValidFreightHandlingCodes] = useState([]);
    const theme = StorefrontAdminTheme;
    const responseMessages = {
        "DeleteForeignKeyConstraintViolation": localized.FreightCodeDeleteForeignKeyConstraintViolation,
        "UpdateForeignKeyConstraintViolation": localized.FreightCodeUpdateForeignKeyConstraintViolation,
        "DuplicateKeyViolation": localized.FreightCodeDuplicateKeyViolation,
        "GenericFailure": localized.FreightCodeGenericFailure,
        "DeleteSuccess": localized.FreightCodeDeleteSuccess,
        "UpdateSuccess": localized.FreightCodeUpdateSuccess,
        "AddSuccess": localized.FreightCodeAddSuccess
    };

    useEffect(() => {
        getFreightHandlingCodes();
    }, []);

    const customToolbar = () => {
        return (
            <GridToolbarContainer>
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{
                        tooltip: { title: 'Filter list' }
                    }}
                    variant="outlined"
                    color="update"
                />
                <GridToolbarDensitySelector
                    slotProps={{
                        tooltip: { title: 'Change density' }
                    }}
                    variant="outlined"
                    color="update"
                />

                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    slotProps={{
                        tooltip: { title: 'Export data' }
                    }}
                    variant="outlined"
                    color="update"
                />
                {renderAddButton()}
            </GridToolbarContainer>
        );
    }

    const getFreightHandlingCodes = () => {
        setFetching(true);
        Storefront.getValidFreightHandlingCodes().then(loadValidFreightHandlingCodes);
        Storefront.getFreightHandlingCodes(storeData.storefrontNumber).then(loaded);
    }

    const updateFreightHandlingCode = async (form, type) => {
        var request = {
            id: form.Id ?? null,
            code: form.Code ?? null,
            storefrontNumber: storeData.storefrontNumber,
            description: form.description ?? null,
            operationType: type
        };
        return await Storefront.updateFreightHandlingCode(request).then((res) => {
            if (res) {      
                const notificationType = res.response.endsWith("Success") ? "message" : "error";
                const message = responseMessages[res.response];

                if (message) {
                    Notifications[notificationType](message);
                }
                else {
                    Notifications.error(localized.FreightCodeFailUpdateNotification);
                }
                getFreightHandlingCodes();
            }   
        });
    }

    const loadValidFreightHandlingCodes = (data) => {
        if (data) {
            setValidFreightHandlingCodes(data.freightHandlingCodes);
        }
    }

    const loaded = (data) => {
        if (data) {
            mapRows(data);
        }
        setFetching(false);
    }

    const mapRows = (data) => {
        var rows = [];
        data.freightHandlingCodes.forEach((freightHandlingCode, index) => {

            rows.push({ id: index, codeId: freightHandlingCode.id, code: freightHandlingCode.code, description: freightHandlingCode.description, });
        });
        setRows(rows);
    }

    const handleChangeFreightCode = (event) => {
        setFreightHandlingCode(event.target.value);
        setDescription(validFreightHandlingCodes.find(f => f.validFreightHandlingCode1 === event.target.value).description);
    } 

    const handleClickOpenUpdate = (e,row) => {    
        e.stopPropagation();
        setSelectedRow(row);
        setOpenUpdate(true);
    };

    const handleCloseUpdate = () => {
        setDescription("");
        setOpenUpdate(false);
    };
    const handleClickOpenDelete = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);    
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleClickOpenAdd = () => {
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setFreightHandlingCode("");
        setDescription("");
        setOpenAdd(false);
    };

    const renderDeleteButton = (row) => (
        <MUIAlertModal
            open={openDelete}
            onClose={handleCloseDelete}
            title={localized.AreYouSure}
            message={
                <>
                    <h3>{localized.AreYouSureDeleteFreightCode}</h3>
                    <DialogContentText>
                        <b>{row.code}</b>
                    </DialogContentText>
                </>
            }
            confirmLabel={localized.StorefrontAdminDelete}
            cancelLabel={localized.StorefrontAdminCancel}
            onConfirm={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("Id", row.codeId);
                const formJson = Object.fromEntries(formData.entries());
                updateFreightHandlingCode(formJson, "delete");
                handleCloseDelete();
            }}
            confirmButtonProps={{ color: "error" }}
            cancelButtonProps={{ color: "update" }}
        />
    );

    const renderUpdateButton = (row) => (
        <MUIModal
            open={openUpdate}
            onClose={handleCloseUpdate}
            title={localized.UpdateFreightHandlingCode}
            onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("Id", row.codeId);
                formData.append("Code", row.code);
                const formJson = Object.fromEntries(formData.entries());
                updateFreightHandlingCode(formJson, "update");
                handleCloseUpdate();
            }}
            submitLabel={localized.StorefrontAdminUpdate}
            cancelLabel={localized.StorefrontAdminCancel}
            showDefaultButtons={true}
            submitButtonProps={{ color: "update" }}
            cancelButtonProps={{ color: "update" }}
            maxWidth="sm"
            fullWidth
        >
            <DialogContentText>
                <h3>{localized.ValuesEligibleForUpdateBelow}</h3>
            </DialogContentText>

            <TextField
                defaultValue={row.description}
                inputProps={{ maxLength: 500 }}
                required
                margin="dense"
                id="description"
                name="description"
                label="Description"
                type="text"
                fullWidth
                variant="standard"
            />
        </MUIModal>
    );

    const renderAddButton = () => (
        <strong>
            <Button
                variant="contained"
                color="success"
                style={{ marginLeft: 16 }}
                onClick={handleClickOpenAdd}
            >
                {localized.StorefrontAdminAdd}
            </Button>
        </strong>
    );


    const renderAddModal = () => (
        <MUIModal
            open={openAdd}
            onClose={handleCloseAdd}
            title={localized.AddFreightHandlingCode}
            onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const formJson = Object.fromEntries(formData.entries());
                updateFreightHandlingCode(formJson, "add");
                handleCloseAdd();
            }}
            submitLabel={localized.StorefrontAdminAdd}
            cancelLabel={localized.StorefrontAdminCancel}
            showDefaultButtons={true}
            submitButtonProps={{ color: "success" }}
            cancelButtonProps={{ color: "error" }}
            maxWidth="sm"
            fullWidth
        >
            <DialogContentText>
                <h3>{localized.FillRequiredFieldsBelow}</h3>
            </DialogContentText>

            <InputLabel required id="freightHandlingCode-label">
                {localized.StorefrontAdminCode}
            </InputLabel>

            <Select
                required
                fullWidth
                labelId="freightHandlingCode-label"
                id="freightHandlingCode"
                name="Code"
                value={freightHandlingCode}
                label={localized.StorefrontAdminCode}
                onChange={handleChangeFreightCode}
                MenuProps={{ disablePortal: true, PaperProps: { sx: { zIndex: 1600 } } }}
            >
                {validFreightHandlingCodes?.map((f) => (
                    <MenuItem key={f.validFreightHandlingCode1} value={f.validFreightHandlingCode1}>
                        {f.validFreightHandlingCode1 + " - " + f.description}
                    </MenuItem>
                ))}
            </Select>

            <TextField
                defaultValue={description}
                inputProps={{ maxLength: 500 }}
                required
                id="description"
                name="description"
                label={localized.StorefrontAdminDescription}
                type="text"
                fullWidth
                variant="standard"
            />
        </MUIModal>
    );


    const columns = [
        {
            field: 'code',
            headerName: localized.StorefrontAdminCode,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'description',
            headerName: localized.StorefrontAdminDescription,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'updateButton',
            headerName: localized.StorefrontAdminUpdate,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="update"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenUpdate(e, params.row)}
                        >
                            {localized.StorefrontAdminUpdate}
                        </Button>
                    </strong>
                )
            },            
            disableClickEventBubbling: true
            
        },
        {
            field: 'deleteButton',
            headerName: localized.StorefrontAdminDelete,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'columnTopper',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="error"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenDelete(e, params.row)}
                        >
                            {localized.StorefrontAdminDelete}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true
        }
    ];

    const freightItems = useMemo(() => {
        return (
            <Paper sx={{ height: 700, width: '100%' }}>
                <TranslatableDataGrid rows={rows} columns={columns} insetToolbar={customToolbar} labels={localized} />
            </Paper>
        );
    }, [rows, localized]);

    return (
        <>
            {
                fetching && <Loading type="brand" />
            }
            {
                !fetching && (localized && Object.keys(localized).length > 0) &&
                <>
                    <ThemeProvider theme={theme}>
                        <Typography variant="h1">{localized.FreightHandlingCodeAdministrationHeader}</Typography>
                        {freightItems}                       
                        {openUpdate && renderUpdateButton(selectedRow)}
                        {openDelete && renderDeleteButton(selectedRow)}
                        {openAdd && renderAddModal()}
                    </ThemeProvider>
                </>
            }
        </>
    );
}

export default FreightHandlingCodeAdministration;

